import type {Announcements} from '@dnd-kit/core';

import {getItemAccessibleName} from '../../shared/accessibleName';
import type {orderingGapText} from '../../shared/TranslationSection/TranslationText';
import type {DragProgress} from '../../shared/dragProgress';
import type {ItemMoveCopy} from '../../shared/itemMoveCopy';
import type {OrderingItem} from '../store';
import {indexAfterMove} from '../store';

/** The index a `divider-<n>` droppable id names, or null for any other droppable. */
export const dividerIndexFrom = (
  droppableId: string | number | null | undefined
): number | null => {
  const id = droppableId?.toString() ?? '';
  if (!id.startsWith('divider-')) return null;
  const index = parseInt(id.split('-')[1], 10);
  return Number.isNaN(index) ? null : index;
};

const MAX_GAP_NAME_WORDS = 5;

export type GapCopy = typeof orderingGapText;

export const englishGapCopy: GapCopy = {
  before: (item) => `Place before ${item}`,
  between: (previous, next) => `Place in between ${previous} and ${next}`,
  after: (item) => `Place after ${item}`,
  unnamedItem: 'Item',
  fallback: 'Insert position'
};

const gapItemName = (name: string, copy: GapCopy): string => {
  const words = name.split(/\s+/).filter(Boolean);
  if (words.length === 0) return copy.unnamedItem;
  return words.length > MAX_GAP_NAME_WORDS
    ? `${words.slice(0, MAX_GAP_NAME_WORDS).join(' ')}\u2026`
    : words.join(' ');
};

/** The spoken name of the insert point at `dividerIndex`, by the items beside it. */
export const insertPointLabel = (
  names: string[],
  dividerIndex: number,
  copy: GapCopy = englishGapCopy
): string => {
  if (names.length === 0) return copy.fallback;
  if (dividerIndex <= 0) return copy.before(gapItemName(names[0], copy));
  if (dividerIndex >= names.length) return copy.after(gapItemName(names[names.length - 1], copy));
  return copy.between(
    gapItemName(names[dividerIndex - 1], copy),
    gapItemName(names[dividerIndex], copy)
  );
};

export const selectedMessage = (copy: ItemMoveCopy, name: string): string =>
  copy.selectedForPosition(name);

export const deselectedMessage = (copy: ItemMoveCopy, name: string): string =>
  copy.deselected(name);

export const backWhereItWasMessage = (copy: ItemMoveCopy, name: string): string =>
  copy.backWhereItWas(name);

export const movedMessage = (
  copy: ItemMoveCopy,
  name: string,
  fromIndex: number,
  toIndex: number,
  total: number
): string =>
  toIndex === fromIndex
    ? backWhereItWasMessage(copy, name)
    : copy.movedToPosition(name, toIndex + 1, total);

/**
 * dnd-kit announcements for the ordering list, spoken through DndContext's own live region.
 * They read `items` as rendered: DndContext runs our drop handler before it announces, so on
 * drop the store has already moved the item, but the pre-move order plus the divider gives the
 * same landing position. An id the list doesn't have is left unannounced.
 *
 * @param gapLabel The spoken name of the gap at a divider index, as its button is named.
 */
export const orderingAnnouncements = (
  copy: ItemMoveCopy,
  items: OrderingItem[],
  progress: DragProgress = {hasLeftOrigin: false},
  gapLabel: (dividerIndex: number) => string = (dividerIndex) => insertPointLabel([], dividerIndex)
): Announcements => {
  const total = items.length;
  const describe = (activeId: string | number): {index: number; name: string} | null => {
    const index = items.findIndex((item) => item.id === activeId);
    return index === -1
      ? null
      : {index, name: getItemAccessibleName(items[index].value) || copy.unnamedItem};
  };

  return {
    onDragStart: ({active}) => {
      const item = describe(active.id);
      if (!item) return undefined;
      progress.hasLeftOrigin = false;
      return selectedMessage(copy, item.name);
    },
    onDragOver: ({active, over}) => {
      const item = describe(active.id);
      if (!item) return undefined;
      const divider = dividerIndexFrom(over?.id);
      if (divider === null) {
        progress.hasLeftOrigin = true;
        return copy.notOverPosition;
      }
      if (indexAfterMove(item.index, divider) === item.index && !progress.hasLeftOrigin) {
        return undefined;
      }
      progress.hasLeftOrigin = true;
      return `${gapLabel(divider)}.`;
    },
    onDragEnd: ({active, over}) => {
      const item = describe(active.id);
      if (!item) return undefined;
      const {index, name} = item;
      const divider = dividerIndexFrom(over?.id);
      if (divider === null) return backWhereItWasMessage(copy, name);
      return movedMessage(copy, name, index, indexAfterMove(index, divider), total);
    },
    onDragCancel: ({active}) => {
      const item = describe(active.id);
      return item ? backWhereItWasMessage(copy, item.name) : undefined;
    }
  };
};
